import {
  AccountType,
  ModelError_Response,
  postV1CompaniesCompanyIdAccountsConnected,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import ConnectAccountByTreadIdForm, {
  ConnectAccountByTreadIdFormHandler,
} from '~pages/Settings/Accounts/ConnectAccountByTreadIdForm/ConnectAccountByTreadIdForm';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

interface ConnectAccountDialogProps {
  isDialogOpen: boolean;
  onClose: () => void;
  onConnected: () => void;
}

export default function ConnectAccountDialog({
  isDialogOpen,
  onClose,
  onConnected,
}: ConnectAccountDialogProps) {
  const { toasterStore } = useStores();
  const connectAccountFormRef = useRef<ConnectAccountByTreadIdFormHandler>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldDisableSubmitCTA, setShouldDisableSubmitCTA] = useState(false);

  const handleFormValidityChange = useCallback((isFormValid: boolean) => {
    setShouldDisableSubmitCTA(!isFormValid);
  }, []);

  const handleSubmit = useCallback(async () => {
    const data = await connectAccountFormRef.current?.onSubmit();

    if (data) {
      try {
        setIsSubmitting(true);

        const accountTypes: AccountType[] = [];

        if (data.isCustomer) {
          accountTypes.push(AccountType.CUSTOMER);
        }

        if (data.isVendor) {
          accountTypes.push(AccountType.VENDOR);
        }

        await postV1CompaniesCompanyIdAccountsConnected({
          path: { 'company-id': data.senderCompany.id },
          body: { account_types: accountTypes, tread_id: data.treadId },
        });

        onClose();
        toasterStore.push(alert(t('account.connection_successful'), AlertTypes.success));
        onConnected();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          const e = error as AxiosError<ModelError_Response>;
          const message = e.response?.data.error.errors?.[0]?.message;

          if (message?.includes('has already been taken')) {
            toasterStore.push(
              alert(
                t('error_messages.account.company_already_connected'),
                AlertTypes.info,
              ),
            );
          } else {
            connection.handleRequestError(
              error,
              t('error_messages.account.failed_to_create_connected_by_tread_id'),
            );
          }
        } else {
          connection.handleRequestError(
            error,
            t('error_messages.account.failed_to_create_connected_by_tread_id'),
          );
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [onClose, onConnected, toasterStore]);

  return (
    <Dialog maxWidth="sm" onClose={onClose} isOpen={isDialogOpen}>
      <DialogHeader>{t('account.add_connection_with_tread_id_heading')}</DialogHeader>

      <DialogContent sx={(theme) => ({ bgcolor: theme.semantic.surfaceSunken })}>
        <ConnectAccountByTreadIdForm
          ref={connectAccountFormRef}
          onFormValidityChange={handleFormValidityChange}
        />
      </DialogContent>

      <DialogActions>
        <SmallButton color="secondary" onClick={onClose} variant="outlined">
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          color="primary"
          disabled={shouldDisableSubmitCTA}
          loading={isSubmitting}
          onClick={handleSubmit}
          variant="contained"
        >
          {t('actions.connect')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}
